// Confirm my email, from the link in a confirm email
// (PLAN-2026-10-18-families-and-newcomers §7.1, scenario K3).
//
// The link is `/confirm-email#<token>`, in the fragment for the reset's reason
// (a browser sends a fragment nowhere), and this screen takes it out of the
// address bar as soon as it has read it. Signed in or not — a phone's mail
// app is usually not signed in — and it signs nobody in.
//
// IT CONFIRMS ON A PRESS, NEVER ON OPENING. Some mail scanners open every link
// in a message in a real browser, scripts and all; a page that confirmed on
// load would let one confirm an address somebody else signed up with, which
// is the mail-to-a-stranger case confirming exists to stop. The stop page
// waits for its press for the same reason (B-443).
//
// One card at a time (the Wizard, as the reset): ask, then confirmed, or
// confirmed already, or a link that will not work, which says where a new
// one comes from.

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSession } from '../data/session';
import { useTier } from '../shell/useTier';
import { Wizard, type WizardStep } from '../templates/Wizard';
import { Text } from '../kit/ui';

/** A token as `newToken` makes them: base64url. */
const TOKEN = /^[A-Za-z0-9_-]{20,}$/;

type Stage = 'ask' | 'working' | 'done' | 'already' | 'failed';

export function ConfirmEmailScreen() {
  const { confirmEmail, session } = useSession();
  const tier = useTier();
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState<string | null>(() => {
    const raw = location.hash.replace(/^#/, '');
    return TOKEN.test(raw) ? raw : null;
  });
  const [stage, setStage] = useState<Stage>(token === null ? 'failed' : 'ask');
  const [why, setWhy] = useState<string>(
    token === null ? 'This page opens from the link in a confirm email.' : '',
  );

  useEffect(() => {
    if (location.hash !== '') navigate(location.pathname, { replace: true });
  }, [location.hash, location.pathname, navigate]);

  const signedIn = session !== null;
  const confirm = async () => {
    if (token === null || stage !== 'ask') return;
    setStage('working');
    const outcome = await confirmEmail(token);
    if (outcome.ok) {
      setStage(outcome.already ? 'already' : 'done');
      return;
    }
    // An older link, retired by a newer one that was used: the address is
    // confirmed, and that is the news, not that this link is spent.
    if (session?.emailConfirmed === true) {
      setStage('already');
      return;
    }
    setWhy(outcome.message);
    setStage('failed');
  };

  const steps: Record<Stage, WizardStep> = {
    ask: {
      id: 'ask',
      title: 'Confirm your email',
      hint: 'Press below to confirm this address is yours.',
      content: (
        <Text tone="secondary" size="sm" block>
          Then your leagues can email you: the week’s recap, and the news you choose in Account →
          Notifications. Everything is always in the bell too.
        </Text>
      ),
      doneLabel: 'Confirm my email',
    },
    working: {
      id: 'working',
      title: 'Confirm your email',
      hint: 'Press below to confirm this address is yours.',
      content: null,
      doneLabel: 'Confirming…',
    },
    done: {
      id: 'done',
      title: 'Your email is confirmed',
      hint: 'Your leagues can email you now: the week’s recap, and the news you choose.',
      content: (
        <Text tone="secondary" size="sm" block>
          What reaches your email is yours to choose in Account → Notifications. Everything is
          always in the bell too.
        </Text>
      ),
      doneLabel: signedIn ? 'Choose what to get' : 'Done',
    },
    already: {
      id: 'already',
      title: 'Your email is confirmed',
      hint: 'Your address is confirmed already, so there is nothing more to do.',
      content: null,
      doneLabel: 'Done',
    },
    failed: {
      id: 'failed',
      title: 'That link will not work',
      hint: signedIn
        ? `${why} Account → Notifications sends a new one.`
        : `${why} A new one comes from Account → Notifications once you sign in.`,
      content: null,
      doneLabel: signedIn ? 'Go to Notifications' : 'Done',
    },
  };

  const home = () => navigate('/', { replace: true });
  return (
    <div className="fsp-frame" data-tier={tier}>
      <Wizard
        title="Your email"
        steps={[steps[stage]]}
        index={0}
        onIndex={() => undefined}
        onDone={() => {
          if (stage === 'ask') {
            void confirm();
            return;
          }
          if (stage === 'working') return;
          if (signedIn && (stage === 'done' || stage === 'failed')) {
            navigate('/account/notifications', { replace: true });
            return;
          }
          home();
        }}
        // Cancel only while there is something to decline.
        onCancel={stage === 'ask' ? home : undefined}
      />
    </div>
  );
}
